import { useRef, useState, type ReactNode } from 'react';

import BpkBottomSheet from '@skyscanner-internal/backpack-web/bpk-component-bottom-sheet';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import BpkIconInfo from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/information-circle';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import classNames from 'classnames';

import STYLES from './SponsoredInfoButton.module.scss';

const BpkIconInfoRTL = withRtlSupport(BpkIconInfo);

const AdInfoIcon = () => <BpkIconInfoRTL className={STYLES.infoIcon} />;
type callToActionType = {
  text?: string;
  bottomSheetContent: Array<{
    title: string;
    description: string;
  }>;
  moreInfoData?: {
    title: string;
    description: string;
  };
  bottomSheetTitle?: string;
  bottomSheetA11yLabel?: string;
};
type InfoButtonProps = {
  children?: ReactNode;
  infoIconCustomColor?: boolean;
  isSponsoredDestinationV2?: boolean;
  sponsoredDestinationV2CreativeId?: string;
  sponsoredDestinationV2TrackingId?: string;
  'aria-hidden'?: boolean;
  ariaLabel?: string;
  callToAction: callToActionType & {
    bottomSheetContent: callToActionType['bottomSheetContent'];
  };
  // Optional callback invoked when the bottom sheet open state changes.
  // Used to coordinate multiple overlays in parent components.
  onOpenChange?: (isOpen: boolean) => void;
};

export const SponsoredInfoButton = ({
  'aria-hidden': ariaHidden = false,
  ariaLabel = undefined,
  callToAction,
  children,
  infoIconCustomColor = false,
  isSponsoredDestinationV2 = false,
  onOpenChange,
  sponsoredDestinationV2CreativeId = '',
  sponsoredDestinationV2TrackingId = '',
}: InfoButtonProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleSheetOpenChange = (isOpen: boolean) => {
    setSheetOpen(isOpen);
    onOpenChange?.(isOpen);

    // Return focus to trigger button when modal closes
    // Use setTimeout with a small delay to ensure DOM has settled and screen readers sync with focus
    if (!isOpen) {
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 100);
    }
  };

  const trackSponsoredDestinationV2AdInfoClick = () => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.SPONSORED_DESTINATION_V2_AD_INFO_CLICKED.NAME,
      strings: {
        creativeId: sponsoredDestinationV2CreativeId,
        trackingId: sponsoredDestinationV2TrackingId,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.SPONSORED_DESTINATION_V2_AD_INFO_CLICKED.NAME,
      creativeId: sponsoredDestinationV2CreativeId,
      trackingId: sponsoredDestinationV2TrackingId,
    });
  };

  return (
    <div
      role="presentation"
      onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <button
        ref={buttonRef}
        className={STYLES.adInfoBtn}
        aria-disabled={false}
        aria-hidden={ariaHidden}
        data-testid="adInfoBtn"
        type="button"
        aria-label={ariaLabel || translate('a11y_Ad_info_learn_more')}
        aria-haspopup="dialog"
        onClick={(e) => {
          logOperationalEvent({ eventName: 'Ad_Info_Button_Clicked' });
          e.stopPropagation();
          e.preventDefault();
          handleSheetOpenChange(true);
          isSponsoredDestinationV2 && trackSponsoredDestinationV2AdInfoClick();
        }}
      >
        {children}
        <div
          className={classNames({
            [STYLES.infoIconCustomColor]: infoIconCustomColor,
            [STYLES.infoIconDefaultColor]: !infoIconCustomColor,
          })}
        >
          <AdInfoIcon />
        </div>
      </button>
      <BpkBottomSheet
        id="SponsoredAdInfoBottomSheet"
        isOpen={sheetOpen}
        onClose={() => handleSheetOpenChange(false)}
        title={callToAction?.bottomSheetTitle || ''}
        closeLabel={translate('ktxt_close')}
        ariaLabel={callToAction?.bottomSheetA11yLabel || ''}
        closeOnScrimClick
        closeOnEscPressed
        paddingStyles={{
          top: 'none',
          bottom: 'none',
        }}
      >
        {callToAction.bottomSheetContent.map((item, index) => (
          // Empty titles are valid (e.g. the lead "why seeing" body sits directly under the
          // bottom sheet title bar) so we fall back to index-based keys for stability.
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className={STYLES.bottomSheetContent}>
            <div className={STYLES.bottomSheetText}>
              {item.title && (
                <div className={STYLES.bottomSheetTitle}>
                  <BpkText textStyle={TEXT_STYLES.heading5}>{item.title}</BpkText>
                </div>
              )}
              <div className={STYLES.bottomSheetDescription}>
                <BpkText textStyle={TEXT_STYLES.bodyDefault}>{item.description}</BpkText>
              </div>
            </div>
          </div>
        ))}
      </BpkBottomSheet>
    </div>
  );
};
